import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, ApiError, type ApiFootballFixture } from "@/api/client";
import { toast } from "sonner";
import { Globe, Search, Loader2, CheckCircle2 } from "lucide-react";

// Format the kickoff so the operator can scan dozens of rows quickly.
function formatKickoff(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString("de-AT", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ImportFixtureDialog({ onImported }: { onImported?: () => void }) {
  const [open, setOpen] = useState(false);
  const [team, setTeam] = useState("");
  const [date, setDate] = useState(""); // YYYY-MM-DD
  const [league, setLeague] = useState(""); // optional, paired with date
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<ApiFootballFixture[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [importingId, setImportingId] = useState<number | null>(null);

  const search = async () => {
    if (!team.trim() && !date) {
      toast.error("Team-Name oder Datum eingeben.");
      return;
    }
    setBusy(true);
    setHasSearched(true);
    try {
      const res = await api.games.adminSearchFixtures({
        team: team.trim() || undefined,
        date: date || undefined,
        league: league ? Number(league) : undefined,
      });
      setResults(res.fixtures ?? []);
      if ((res.fixtures ?? []).length === 0) {
        toast.info("Keine Spiele gefunden.");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Suche fehlgeschlagen");
      setResults([]);
    } finally {
      setBusy(false);
    }
  };

  const importOne = async (f: ApiFootballFixture) => {
    setImportingId(f.id);
    try {
      await api.games.adminImportFixture(f.id);
      toast.success(`Importiert: ${f.homeTeam} vs ${f.awayTeam} 📺`);
      // Mark this row as imported locally so the badge swaps instantly
      setResults((rs) => rs.map((r) => (r.id === f.id ? { ...r, alreadyImported: true } : r)));
      onImported?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Import fehlgeschlagen");
    } finally {
      setImportingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="font-bold gap-1.5">
          <Globe className="h-4 w-4" />
          Spiel aus API-Football importieren
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-accent" /> API-Football Import
          </DialogTitle>
          <DialogDescription>
            Finde ein Spiel (z. B. <i>Arsenal vs Burnley</i> heute 21:00) und
            importiere es. Das Spiel wird mit echter externalId angelegt — der
            Live-Cron synct Spielstand & Minute automatisch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 border-b border-border pb-3">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Team-Name</label>
              <Input value={team} onChange={(e) => setTeam(e.target.value)} placeholder="z. B. Arsenal" className="mt-1" />
            </div>
            <div className="self-end text-xs text-muted-foreground hidden sm:block px-2 pb-2">— oder —</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Datum</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">League-ID (optional)</label>
              <Input type="number" value={league} onChange={(e) => setLeague(e.target.value)} placeholder="z. B. 39 (PL)" className="mt-1" />
            </div>
            <Button onClick={search} disabled={busy} className="bg-accent text-accent-foreground hover:bg-accent/90 font-bold gap-1.5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Suchen
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Tipp: Team-Suche durchsucht die nächsten 30 Tage. Bei Datum + League-ID werden alle Spiele dieser Liga an dem Tag aufgelistet.
            League-IDs: 39 (Premier League), 78 (Bundesliga), 135 (Serie A), 140 (La Liga), 218 (Bundesliga AT), 2 (Champions League).
          </p>
        </div>

        <div className="flex-1 overflow-y-auto min-h-[120px]">
          {!hasSearched && (
            <div className="text-center text-sm text-muted-foreground py-10">Noch keine Suche. Team-Name oder Datum eingeben und „Suchen" drücken.</div>
          )}
          {hasSearched && results.length === 0 && !busy && (
            <div className="text-center text-sm text-muted-foreground py-10">Keine Spiele gefunden.</div>
          )}
          {results.length > 0 && (
            <ul className="divide-y divide-border">
              {results.map((f) => (
                <li key={f.id} className="py-2.5 px-1 flex items-center gap-3">
                  {f.leagueLogo && <img src={f.leagueLogo} alt="" className="w-6 h-6 rounded shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground truncate">
                      {f.leagueName} · {f.round}
                    </div>
                    <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
                      {f.homeLogo && <img src={f.homeLogo} alt="" className="w-4 h-4 inline" />}
                      <span>{f.homeTeam}</span>
                      <span className="text-muted-foreground">vs</span>
                      {f.awayLogo && <img src={f.awayLogo} alt="" className="w-4 h-4 inline" />}
                      <span>{f.awayTeam}</span>
                      {f.statusShort && ["FT", "AET", "PEN"].includes(f.statusShort) && f.homeGoals != null && (
                        <span className="text-xs font-bold bg-muted px-1.5 py-0.5 rounded ml-1">
                          {f.homeGoals}:{f.awayGoals}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {formatKickoff(f.kickoffTime)}
                      {f.venue && <> · {f.venue}</>}
                    </div>
                  </div>
                  {f.alreadyImported ? (
                    <span className="text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-pill px-2 py-1 inline-flex items-center gap-1 shrink-0">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Importiert
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => importOne(f)}
                      disabled={importingId === f.id}
                      className="h-8 bg-accent text-accent-foreground hover:bg-accent/90 font-bold shrink-0"
                    >
                      {importingId === f.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Importieren"}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
